import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, EMPTY, of, switchMap, map, catchError, filter, BehaviorSubject, tap, forkJoin, combineLatest } from 'rxjs';
import { PaginationService } from '../pagination.service';
import { DatabaseAction, EntityType } from '../../objects/interface-library';
import { Contract } from '../../objects/contract'; // Assuming Contract is defined here
import { ContractHydrated, ContractPageResponse } from '../../objects/contract-hydrated';
import { ContractHydrationFieldValue, ContractHydrationField } from '../../objects/contract-hydration';
import { RehydrationService } from '../entity-data-services/rehydration.service';
import { PermissionsStoreService } from '../permissions/permissions-store.service';
import { PermissionsUtilityService } from '../permissions/permissions-utility.service';
import { UsersService } from './users.service';

@Injectable({
  providedIn: 'root'
})
export class ContractService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);
  private rehydrationService = inject(RehydrationService);
  private permissionsStore = inject(PermissionsStoreService);
  private permissionsUtility = inject(PermissionsUtilityService);
  private usersService = inject(UsersService);

  private readonly headers = new HttpHeaders({
    'Content-Type': 'application/json'
  });

  private allContractsSubject = new BehaviorSubject<Contract[]>([]);
  public readonly allContracts$: Observable<Contract[]> = this.allContractsSubject.asObservable();

  private visibleContractsSubject = new BehaviorSubject<Contract[]>([]);
  public readonly visibleContracts$: Observable<Contract[]> = this.visibleContractsSubject.asObservable();

  private archivedContractsSubject = new BehaviorSubject<Contract[]>([]);
  public readonly archivedContracts$: Observable<Contract[]> = this.archivedContractsSubject.asObservable();

  constructor() {
    // Fetch contracts with FULL hydration by default to get complete objects
    const hydrateFields: ContractHydrationFieldValue[] = Object.values(ContractHydrationField);
    this.refreshAllContractsRecords(hydrateFields);
    this.setupVisibilityFiltering();
  }

  private static readonly ARCHIVED_STATUSES = ['Retired', 'Archived', 'Superseded', 'Terminated', 'Cancelled'];

  /**
   * Determines if a contract should be considered archived.
   * A contract is archived if activeContract is false OR status matches an archived status.
   */
  private isArchivedContract(contract: Contract): boolean {
    if ((contract as any).activeContract === false) return true;
    if (contract.status && ContractService.ARCHIVED_STATUSES.includes(contract.status)) return true;
    return false;
  }

  private setupVisibilityFiltering(): void {
    this.allContracts$.subscribe(c => console.log('[ContractService] upstream allContracts$ emit:', c.length));
    toObservable(this.permissionsStore.permissions).subscribe(p => console.log('[ContractService] upstream permissions emit:', p ? 'has-permissions' : p));
    this.usersService.currentUserEmail$.subscribe(e => console.log('[ContractService] upstream currentUserEmail$ emit:', e));

    combineLatest([
      this.allContracts$,
      toObservable(this.permissionsStore.permissions),
      this.usersService.currentUserEmail$
    ]).subscribe(([contracts, permissions, currentUserEmail]) => {
      console.log('[ContractService] combineLatest fired — contracts:', contracts.length, 'permissions:', permissions ? 'yes' : permissions, 'email:', currentUserEmail);
      let permittedContracts: Contract[];

      if (this.isViewerLike(permissions)) {
        // View-only / not-yet-permissioned users: only contracts they own (+ any
        // metadata an admin explicitly assigned). Empty limits = owned-only, NOT all.
        permittedContracts = this.permissionsUtility.filterContractsForViewer(contracts as any, permissions as any, currentUserEmail) as Contract[];
      } else if (!permissions || this.hasNoPermissionLimits(permissions)) {
        permittedContracts = contracts;
      } else {
        permittedContracts = this.permissionsUtility.filterContracts(contracts as any, permissions, currentUserEmail) as Contract[];
      }

      // Split into active and archived
      const active = permittedContracts.filter(c => !this.isArchivedContract(c));
      const archived = permittedContracts.filter(c => this.isArchivedContract(c));

      console.log('[ContractService] visibleContractsSubject.next() — active:', active.length, 'archived:', archived.length);
      this.visibleContractsSubject.next(active);
      this.archivedContractsSubject.next(archived);
    });
  }

  /**
   * True for a view-only or not-yet-permissioned user. These users must be
   * restricted to contracts they own (the "empty limits = see all" shortcut does
   * NOT apply to them). Covers the explicit 'Viewer' role and the defensive
   * no-role case (e.g. an activated user whose role hasn't been assigned yet, or
   * the brief window before permissions load) — both default to least privilege.
   * Any other (privileged) role keeps the normal behavior.
   */
  private isViewerLike(permissions: any): boolean {
    const role = (permissions?.role ?? '').toString().toLowerCase().trim();
    return role === '' || role === 'viewer';
  }

  /**
   * Checks if the user has no permission limits (i.e., can see all contracts).
   * A user has no limits if all permission arrays are empty or undefined.
   */
  private hasNoPermissionLimits(permissions: any): boolean {
    return (!permissions.legalEntities || permissions.legalEntities.length === 0) &&
           (!permissions.contractTypes || permissions.contractTypes.length === 0) &&
           (!permissions.departments || permissions.departments.length === 0) &&
           (!permissions.sites || permissions.sites.length === 0) &&
           (!permissions.databaseOrganizations || permissions.databaseOrganizations.length === 0);
  }

  /**
   * Normalizes a contract object to ensure consistent flat structure.
   *
   * NEW API (Phase 1): Returns flat data directly - no wrapper
   * OLD API (legacy): Returns { contractData: {...}, contractNum, ... }
   *
   * This method handles both formats for backwards compatibility during migration.
   */
  private flattenContract(contract: any): Contract {
    if (!contract) return contract;

    // NEW API: Already flat - has contractId at root level without contractData wrapper
    if (contract.contractId && !contract.contractData) {
      return contract as Contract;
    }

    // OLD API: Has contractData wrapper - flatten it
    if (contract.contractData) {
      const { contractData, ...metadata } = contract;
      const preferredContractId = contractData.contractId || metadata.contractId;

      return {
        ...contractData,
        ...metadata,
        contractId: preferredContractId
      } as Contract;
    }

    // Fallback: assume already flat
    return contract as Contract;
  }

  /**
   * Private method to fetch a single page of contracts for the pagination service.
   * This method has been updated to use POST and send the key in the body.
   * Supports API hydration via the hydrate query parameter.
   *
   * @param lastEvaluatedKey The key from the last page for the next query.
   * @param hydrateFields Optional array of field names to hydrate (e.g., ['directVendor', 'orgContractAdmin'])
   * @returns An Observable of the paginated response.
   *
   * @example
   * // Without hydration - returns IDs only
   * getContractsByPagination(null)
   *
   * // With hydration - returns full objects for specified fields
   * getContractsByPagination(null, ['directVendor', 'orgContractAdmin', 'sites'])
   */
  private getContractsByPagination(
    lastEvaluatedKey: any = null,
    hydrateFields?: ContractHydrationFieldValue[]
  ): Observable<ContractPageResponse> {
    let url = environment.URL_CONTRACTS_PAGE_OF_CONTRACTS;

    // Add hydration query parameter if fields are specified
    if (hydrateFields && hydrateFields.length > 0) {
      const hydrateParam = hydrateFields.join(',');
      url += `?hydrate=${encodeURIComponent(hydrateParam)}`;
    }

    const body = { lastEvaluatedKey };

    console.log('[ContractService] HTTP POST →', url, 'body:', body);
    return this.http.post<ContractPageResponse>(url, body, { headers: this.headers }).pipe(
      tap((response: any) => console.log('[ContractService] HTTP response page items:', response?.items?.length, 'lastEvaluatedKey:', response?.lastEvaluatedKey)),
      map((response: any) => ({
        items: response.items as ContractHydrated[],
        lastEvaluatedKey: response.lastEvaluatedKey
      })),
      catchError(error => {
        console.error('[ContractService] HTTP ERROR fetching contracts page from', url, '— status:', error?.status, 'message:', error?.message, 'full:', error);
        return of({ items: [], lastEvaluatedKey: null });
      })
    );
  }
  
  /**
   * Public method to refresh all contracts by calling the paginated API and updating the subject.
   *
   * @param hydrateFields Optional array of field names to hydrate when fetching contracts.
   *                      When provided, the API returns full objects instead of just IDs for these fields.
   *
   * @example
   * // Fetch without hydration (default behavior)
   * refreshAllContractsRecords()
   *
   * // Fetch with specific fields hydrated
   * refreshAllContractsRecords(['directVendor', 'orgContractAdmin'])
   *
   * // Fetch with preset hydration fields
   * import { ContractHydrationPresets } from '../../objects/contract-hydration';
   * refreshAllContractsRecords(ContractHydrationPresets.BASIC)
   */
  public refreshAllContractsRecords(hydrateFields?: ContractHydrationFieldValue[]): void {
    this.paginationService.getAllPaginatedDataProgressively(
      key => this.getContractsByPagination(key, hydrateFields)
    )
      .pipe(
        // FIRST: Flatten contracts to move contractData fields to root level
        // This must happen BEFORE hydration so RehydrationService can find the fields
        map(contracts => contracts.map(c => this.flattenContract(c))),
        // THEN: Client-side hydration to replace IDs with full objects
        switchMap(contracts => {
          if (contracts.length === 0) {
            return of([]);
          }

          // Hydrate each contract using the RehydrationService
          return forkJoin(
            contracts.map(contract => this.rehydrationService.hydrateContract(contract as Contract))
          );
        }),
        map(items => items as Contract[]),
        catchError(error => {
          console.error('Error fetching/hydrating contracts:', error);
          // Keep just-written contracts even when the refresh fails — otherwise a
          // transient error silently erases the record the user just created.
          this.allContractsSubject.next(this.mergePendingContracts([]));
          return EMPTY;
        })
      )
      .subscribe(allContracts => {
        console.log('[ContractService] allContractsSubject.next() with', allContracts.length, 'contracts (after hydration)');
        // Contracts are already flattened before hydration
        this.allContractsSubject.next(this.mergePendingContracts(allContracts));
        // visibleContractsSubject will update automatically via setupVisibilityFiltering
      });
  }

  /**
   * Contracts written this session that the server's scan/hydration hasn't
   * returned yet. Merged into every emission so a freshly-saved contract can't be
   * erased by an in-flight refresh, and dropped once the server's copy arrives.
   */
  private pendingContracts = new Map<string, Contract>();

  /** Put a just-written contract into the live cache immediately. */
  private upsertContractInCache(contract: Contract): void {
    if (!contract?.contractId) return;
    this.pendingContracts.set(contract.contractId, contract);
    this.allContractsSubject.next(this.mergePendingContracts(this.allContractsSubject.getValue()));
  }

  /** Overlay pending writes on a server list; server copy wins once it appears. */
  private mergePendingContracts(serverList: Contract[]): Contract[] {
    if (!this.pendingContracts.size) return serverList;

    const byId = new Map<string, Contract>();
    serverList.forEach(c => { if (c?.contractId) byId.set(c.contractId, c); });

    for (const [contractId, pending] of Array.from(this.pendingContracts.entries())) {
      const fromServer = byId.get(contractId);
      if (fromServer) {
        // Server has it (and its version is hydrated) — reconciled, stop tracking.
        byId.set(contractId, fromServer);
        this.pendingContracts.delete(contractId);
      } else {
        byId.set(contractId, pending);
      }
    }

    const keyed = new Set(Array.from(byId.values()));
    return [...Array.from(byId.values()), ...serverList.filter(c => !c?.contractId && !keyed.has(c))];
  }



  /**
   * Public method to fetch a single page of contracts with optional API hydration.
   * This method can be used by components that need direct control over pagination and hydration.
   *
   * @param lastEvaluatedKey The pagination key from the previous page (null for first page)
   * @param hydrateFields Optional array of field names to hydrate via the API
   * @returns An Observable containing the page of contracts and pagination key
   *
   * @example
   * // Fetch first page without hydration
   * getPageOfContracts(null).subscribe(response => {
   *   console.log('Contracts:', response.items);
   *   console.log('Next key:', response.lastEvaluatedKey);
   * });
   *
   * // Fetch with hydration
   * import { ContractHydrationPresets } from '../../objects/contract-hydration';
   * getPageOfContracts(null, ContractHydrationPresets.BASIC).subscribe(response => {
   *   // response.items will have directVendor and orgContractAdmin as full objects
   * });
   */
  public getPageOfContracts(
    lastEvaluatedKey: any = null,
    hydrateFields?: ContractHydrationFieldValue[]
  ): Observable<ContractPageResponse> {
    return this.getContractsByPagination(lastEvaluatedKey, hydrateFields);
  }

  /**
   * Returns a single, fully hydrated contract object from the cache.
   * This method now relies on the `allContracts$` observable to get its data.
   * @param contractId The unique ID of the contract.
   * @returns An Observable of the found Contract, or `null` if not found.
   */
  public getContractFullyHydrated(contractId: string): Observable<Contract | null> {
    return this.allContracts$.pipe(
      map(contracts => contracts.find(contract => contract.contractId === contractId) || null),
      switchMap(contract => {
        if (!contract) {
          return of(null);
        }
        return this.rehydrationService.hydrateContract(contract);
      }),
      catchError(error => {
        console.error(`ContractService | Error getting hydrated contract with ID ${contractId}:`, error);
        return of(null);
      })
    );
  }

  /**
   * Fetches the column names for the contracts accordion.
   * @param columns An array of column names.
   * @returns An observable that emits the API response.
   */
  getContractsAccordionDataColumns(columns: string[]): Observable<any> {
    const uniqueColumns = [...new Set(columns)];
    let params = new HttpParams().set('columns', uniqueColumns.toString());

    return this.http.get(environment.contractsAccordionColsUrl, { params: params })
      .pipe(
        catchError(error => {
          console.error('Error fetching accordion columns:', error);
          return EMPTY;
        })
      );
  }

  /**
   * Fetches inactive contracts.
   * @returns An observable of the API response.
   */
  getInactiveContracts(): Observable<any> {
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    return this.http.get(environment.contractsInactiveUrl, options).pipe(
      catchError(error => {
        console.error('An error occurred while fetching inactive contracts:', error);
        return EMPTY;
      })
    );
  }

  /**
   * Updates a contract attribute.
   * @param contractNum The contract number.
   * @param attribute The attribute to update.
   * @param attributeValue The new value for the attribute.
   * @returns An Observable of the API response.
   */
  updateContractAttribute(contractNum: any, attribute: any, attributeValue: any): Observable<any> {
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    const jsonReadyBody = {
      contractNum,
      attribute,
      value: attributeValue
    };
    return this.http.put(environment.contractUpdateAttributeUrl, jsonReadyBody, options)
      .pipe(
        tap(() => {
          // Update the local cache with the new attribute value
          const currentContracts = this.allContractsSubject.getValue();
          const updatedContracts = currentContracts.map(contract => {
            if (contract.contractNum === contractNum) {
              return { ...contract, [attribute]: attributeValue };
            }
            return contract;
          });
          this.allContractsSubject.next(updatedContracts);
          // visibleContractsSubject will update automatically via setupVisibilityFiltering
        }),
        catchError(error => {
          console.error('contract.service | An error occurred during attribute update:', error);
          return EMPTY;
        })
      );
  }

  /**
   * Writes a contract to the database.
   *
   * NEW API (Phase 1):
   * - Uses POST with flat data (no wrapper)
   * - If no contractId: CREATE - backend generates ID (format CON-xxx)
   * - If has contractId: UPDATE - partial update, only sent fields modified
   * - Returns { contractId, message } on success
   *
   * @param contract The contract object to write.
   * @param state The state of the contract.
   * @param action The database action (CREATE or UPDATE).
   * @returns An observable of the API response containing contractId.
   */
  public writeContract(
    contract: Contract,
    state: string,
    action: DatabaseAction
  ): Observable<any> {
    const url = environment.contractWriteUrl;
    const isCreate = action === DatabaseAction.Create || !contract.contractId;

    // Clean the contract object - convert empty strings to null
    const cleanedContract = Object.fromEntries(
      Object.entries(contract).map(([key, value]) => [key, value === '' ? null : value])
    );

    // NEW API: Send flat data directly (no contractData wrapper)
    // For CREATE: omit contractId (backend generates it)
    // For UPDATE: include contractId to identify the record
    const payload = isCreate
      ? { ...cleanedContract, contractId: undefined }
      : cleanedContract;

    // Use POST for both create and update (new API pattern)
    return this.http.post(url, payload, { headers: this.headers }).pipe(
      map((response: any) => {
        // NEW API returns { contractId, message }
        if (isCreate && response?.contractId) {
          // For creates, merge the backend-generated ID back into the contract
          // console.log('ContractService.writeContract: Created new contract with ID:', response.contractId);
        }
        return response;
      }),
      tap((response: any) => {
        // Show it NOW. The refresh below re-scans and then hydrates every contract
        // against the party data (~4,500 rows / ~45 sequential pages for cbhi), so
        // waiting on it leaves the contract missing from lists and pickers.
        const saved = { ...contract } as Contract;
        if (isCreate && response?.contractId) {
          // Creates get their id from the backend — without it the cache entry has
          // no identity and the reconcile below could never match it.
          saved.contractId = response.contractId;
        }
        this.upsertContractInCache(saved);

        // Still refresh, to reconcile with the server's hydrated version.
        const hydrateFields: ContractHydrationFieldValue[] = Object.values(ContractHydrationField);
        this.refreshAllContractsRecords(hydrateFields);
      }),
      catchError(error => {
        const errorContext = {
          contractId: contract.contractId,
          contractName: contract.nameOfContract,
          action,
          state,
          isCreate,
          status: error.status,
          message: error.message
        };

        if (error.status === 400) {
          console.error('ContractService.writeContract: Bad request - validation failed', errorContext);
        } else if (error.status === 401 || error.status === 403) {
          console.error('ContractService.writeContract: Authentication/authorization failed', errorContext);
        } else if (error.status >= 500) {
          console.error('ContractService.writeContract: Server error', errorContext, error);
        } else {
          console.error('ContractService.writeContract: Request failed', errorContext, error);
        }

        throw error; // Re-throw so component can handle it
      })
    );
  }



  /**
   * Creates a new version of a contract (replace).
   * True CREATE: strips both contractId and contractNum so backend generates fresh IDs.
   * The old and new contracts are linked via versionList after creation.
   */
  public createReplacementContract(contract: Contract): Observable<any> {
    const url = environment.contractWriteUrl;

    // Clean the contract - convert empty strings to null
    const cleanedContract = Object.fromEntries(
      Object.entries(contract).map(([key, value]) => [key, value === '' ? null : value])
    );

    // Strip both IDs so backend creates a brand new record
    const payload = { ...cleanedContract, contractId: undefined, contractNum: undefined };

    return this.http.post(url, payload, { headers: this.headers }).pipe(
      map((response: any) => {
        // console.log('ContractService.createReplacementContract: Response:', response);
        return response;
      }),
      tap(() => {
        const hydrateFields: ContractHydrationFieldValue[] = Object.values(ContractHydrationField);
        this.refreshAllContractsRecords(hydrateFields);
      }),
      catchError(error => {
        console.error('ContractService.createReplacementContract: Failed', error);
        throw error;
      })
    );
  }

  /**
   * Writes a contract extension to the database.
   * @param type The type of the extension.
   * @param contractId The ID of the contract.
   * @param extensionName The name of the extension.
   * @param extensionProperties The properties of the extension.
   * @returns An observable of the API response.
   */
  public writeContractExtension(
    type: any,
    contractId: string,
    extensionName: string,
    extensionProperties: any
  ): Observable<any> {
    const jsonReadyBody = {
      'type': type,
      'contractId': contractId,
      'extensionName': extensionName,
      'extensionProperties': extensionProperties
    };

    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return this.http.put(environment.URL_CONTRACT_EXTENSION_WRITE, jsonReadyBody, options)
      .pipe(
        catchError(error => {
          console.error('An error occurred during contract extension write:', error);
          return EMPTY;
        })
      );
  }

  /**
   * Fetches a single contract by its contractNum (partition key)
   * @param contractNum - The contract number (unix timestamp like "1732145678901")
   * @returns Observable of Contract or null if not found
   */
  public getContractByNumber(contractNum: string): Observable<Contract | null> {
    const url = `${environment.contractGetByNumberUrl}${contractNum}`;
    return this.http.get<any>(url, { headers: this.headers })
      .pipe(
        map(response => response ? this.flattenContract(response) : null),
        catchError(error => {
          console.error('Error fetching contract by number:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetches a single contract by its contractId (business ID)
   * If multiple contracts share the same contractId, returns the one with highest version
   * @param contractId - The business contract ID (format like "ABC12345")
   * @returns Observable of Contract or null if not found
   */
  public getContractById(contractId: string): Observable<Contract | null> {
    const url = `${environment.contractGetByIdUrl}${contractId}`;
    return this.http.get<any>(url, { headers: this.headers })
      .pipe(
        map(response => response ? this.flattenContract(response) : null),
        catchError(error => {
          console.error('Error fetching contract by ID:', error);
          return of(null);
        })
      );
  }

  /**
   * Returns contracts where any of the given vendorNums appears as `directVendor`
   * or in `otherParties[]`. Pulls from `visibleContracts` (status-active only,
   * see ARCHIVED_STATUSES) and additionally drops contracts whose
   * `expirationDate` is in the past. Used by the timecards activation/editor
   * flow — admins should never be able to attach an HCP to a contract that
   * is archived or expired.
   *
   * `targetVendorNums` should typically be `[partyNum, parentVendorNum]` so
   * we match either the HCP's own party (direct contractor case) or its
   * parent agency (most common HCP-under-agency case).
   *
   * No-expiration contracts are kept (they're active and unbounded).
   *
   * `includeExpired` (default false, preserving the timecards behavior above)
   * keeps past-expiration contracts in the result. The attach-a-document flow
   * passes true: an addendum or a final invoice can legitimately be filed
   * against a contract that has already run out, and refusing to show it just
   * makes the document unfileable. Callers that opt in are expected to mark
   * expired rows in their UI so the choice stays informed. Archived-status
   * contracts are excluded either way — this reads `visibleContracts`.
   */
  public contractsForParty(
    targetVendorNums: (string | null | undefined)[],
    includeExpired = false
  ): Contract[] {
    const targets = new Set(
      targetVendorNums.filter((n): n is string => !!n).map(n => n.trim()).filter(n => n)
    );
    if (targets.size === 0) return [];

    const extract = (vendor: any): string | null => {
      if (!vendor) return null;
      if (typeof vendor === 'string') return vendor;
      return vendor.vendorNum || vendor.vendorData?.vendorNum || null;
    };

    // Compare on a y/m/d boundary so a contract that expires "today" still
    // counts as active for the whole day (no surprise drops mid-session).
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const notExpired = (c: Contract): boolean => {
      if (!c.expirationDate) return true;
      const exp = new Date(c.expirationDate);
      if (isNaN(exp.getTime())) return true;  // unparseable → don't silently drop
      return exp.getTime() >= todayStart.getTime();
    };

    return this.visibleContractsSubject.getValue().filter(c => {
      if (!includeExpired && !notExpired(c)) return false;
      const direct = extract(c.directVendor);
      if (direct && targets.has(direct)) return true;
      const others = Array.isArray(c.otherParties) ? c.otherParties : [];
      return others.some(p => {
        const num = extract(p);
        return !!num && targets.has(num);
      });
    });
  }

  /**
   * Synchronizes associated contracts based on primary party (directVendor).
   * When contracts share the same primary party, they become associated with each other.
   *
   * @param contractId - The contractId of the contract being saved
   * @param newVendorNum - The vendorNum of the new primary party
   * @param oldVendorNum - The vendorNum of the previous primary party (if changed)
   * @returns Observable with count of updated contracts
   */
  public syncAssociatedContracts(
    contractId: string,
    newVendorNum: string,
    oldVendorNum?: string
  ): Observable<{ updated: number }> {
    const allContracts = this.allContractsSubject.getValue();

    // Helper to extract vendorNum from various formats
    const extractVendorNum = (vendor: any): string | null => {
      if (!vendor) return null;
      if (typeof vendor === 'string') return vendor;
      return vendor.vendorNum || null;
    };

    // Find all contracts that share the NEW primary party (excluding this contract)
    const contractsWithNewParty = allContracts.filter(c => {
      if (c.contractId === contractId) return false; // Exclude self
      const vendorNum = extractVendorNum(c.directVendor);
      return vendorNum === newVendorNum;
    });

    // Find all contracts that share the OLD primary party (if party changed)
    const contractsWithOldParty = oldVendorNum && oldVendorNum !== newVendorNum
      ? allContracts.filter(c => {
          if (c.contractId === contractId) return false; // Exclude self
          const vendorNum = extractVendorNum(c.directVendor);
          return vendorNum === oldVendorNum;
        })
      : [];

    const writeOperations: Observable<any>[] = [];

    // STEP 1: Remove this contract from old party contracts' associatedContracts
    for (const contract of contractsWithOldParty) {
      const currentAssociations = contract.associatedContracts || [];
      if (currentAssociations.includes(contractId)) {
        const updatedAssociations = currentAssociations.filter(id => id !== contractId);
        const updatedContract = { ...contract, associatedContracts: updatedAssociations };
        writeOperations.push(
          this.writeContractSilent(updatedContract).pipe(
            catchError(err => {
              console.error(`Failed to remove association from contract ${contract.contractId}:`, err);
              return of(null);
            })
          )
        );
      }
    }

    // STEP 2: Add this contract to new party contracts' associatedContracts
    const newPartyContractIds: string[] = [];
    for (const contract of contractsWithNewParty) {
      newPartyContractIds.push(contract.contractId!);
      const currentAssociations = contract.associatedContracts || [];
      if (!currentAssociations.includes(contractId)) {
        const updatedAssociations = [...new Set([...currentAssociations, contractId])];
        const updatedContract = { ...contract, associatedContracts: updatedAssociations };
        writeOperations.push(
          this.writeContractSilent(updatedContract).pipe(
            catchError(err => {
              console.error(`Failed to add association to contract ${contract.contractId}:`, err);
              return of(null);
            })
          )
        );
      }
    }

    // STEP 3: Update this contract with all associated contractIds from new party
    // This will be done by the caller since we need to return the list

    if (writeOperations.length === 0) {
      return of({ updated: 0, associatedContractIds: newPartyContractIds } as any);
    }

    return forkJoin(writeOperations).pipe(
      map(results => {
        const successCount = results.filter(r => r !== null).length;
        return { updated: successCount, associatedContractIds: newPartyContractIds } as any;
      }),
      tap(() => {
        // Refresh the contracts cache after all writes complete
        const hydrateFields: ContractHydrationFieldValue[] = Object.values(ContractHydrationField);
        this.refreshAllContractsRecords(hydrateFields);
      }),
      catchError(error => {
        console.error('ContractService.syncAssociatedContracts: Error during sync', error);
        return of({ updated: 0, associatedContractIds: newPartyContractIds } as any);
      })
    );
  }

  /**
   * Gets all contractIds that share the same primary party as the given vendorNum.
   * Excludes the specified contractId from results.
   *
   * @param vendorNum - The vendorNum of the primary party
   * @param excludeContractId - The contractId to exclude from results
   * @returns Array of contractIds
   */
  public getAssociatedContractIds(vendorNum: string, excludeContractId: string): string[] {
    const allContracts = this.allContractsSubject.getValue();

    const extractVendorNum = (vendor: any): string | null => {
      if (!vendor) return null;
      if (typeof vendor === 'string') return vendor;
      return vendor.vendorNum || null;
    };

    const matchingContracts = allContracts.filter(c => {
      if (c.contractId === excludeContractId) return false;
      const cVendorNum = extractVendorNum(c.directVendor);
      return cVendorNum === vendorNum;
    });

    return matchingContracts
      .map(c => c.contractId!)
      .filter(id => id); // Remove any undefined/null
  }

  /**
   * Silent contract write that doesn't refresh the cache (used for batch operations)
   * Uses NEW API: POST with flat data, no wrapper
   */
  private writeContractSilent(contract: Contract): Observable<any> {
    if (!contract?.contractId) {
      console.error('ContractService.writeContractSilent: Missing required contractId for update');
      return of(null);
    }

    const url = environment.contractWriteUrl;

    const cleanedContract = Object.fromEntries(
      Object.entries(contract).map(([key, value]) => [key, value === '' ? null : value])
    );

    // NEW API: POST with flat data (no wrapper)
    return this.http.post(url, cleanedContract, { headers: this.headers }).pipe(
      catchError(error => {
        console.error('ContractService.writeContractSilent: Write failed', {
          contractId: contract.contractId,
          error: error.message
        });
        throw error;
      })
    );
  }

  // ── SANDBOX-ONLY renewal plan preview ──────────────────────────────────────
  // What the nightly contract job (hc-all-orgs-scheduled-ops) will do to this
  // contract and when: auto-renew, expire, or the Action Date email. The rules
  // live in the backend only, so the panel can't drift from the job. Rides on
  // URL_SANDBOX_REMINDER_TEST, which only environment.sandbox-ng.ts sets; without
  // it this resolves to null with no request. Errors surface as { error } — a
  // tester needs to see why there's no answer.
  public getSandboxContractPlan(contractId: string): Observable<any | null> {
    const base = (environment as any).URL_SANDBOX_REMINDER_TEST;
    if (!base) return of(null);
    return this.http.get<any>(base + 'contract-plan/' + encodeURIComponent(contractId), { headers: this.headers }).pipe(
      catchError(err => of({
        error: err?.error?.error || err?.error?.message ||
               (err?.status === 404 ? 'This contract is not in sandbox (404).' : null) ||
               (err?.status === 401 || err?.status === 403 ? 'Not signed in to sandbox — sign out and back in.' : null) ||
               `Request failed (${err?.status ?? 'network'})`
      }))
    );
  }

}
